import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminNav } from '../components/AdminNav';
import { Modal } from '../components/Modal';
import { useAdminApi, AdminApiError } from '../api/useAdminApi';

interface RoomDoc { roomId: string; displayName: string; active: boolean; mailbox: string; timeZone: string; capacity?: number }

// Paired-kiosk doc shape returned by GET /api/manage/bindings
// (admin-bindings.ts). Shared-Web-Clip pairing model — the iPad self-claims
// at /kiosk and an admin assigns it a room from this page.
interface AdminBindingView {
  id: string;
  claimCode: string;
  status: 'unbound' | 'bound' | 'revoked';
  roomId: string | null;
  online: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  boundAt: string | null;
  boundBy: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  userAgent: string | null;
}

const COMMANDS = ['reload', 'blank', 'clear'] as const;

export function AdminDevices() {
  const { apiFetch, signedIn } = useAdminApi();
  const qc = useQueryClient();

  const [assignBindingFor, setAssignBindingFor] = useState<AdminBindingView | null>(null);
  const [assignRoomId, setAssignRoomId] = useState<string>('');
  const [revokeBindingFor, setRevokeBindingFor] = useState<AdminBindingView | null>(null);
  const [deleteBindingFor, setDeleteBindingFor] = useState<AdminBindingView | null>(null);
  const [commandBindingFor, setCommandBindingFor] = useState<AdminBindingView | null>(null);
  const [chosenBindingCommand, setChosenBindingCommand] = useState<'reload' | 'blank' | 'clear'>('reload');

  const roomsQ = useQuery({
    queryKey: ['admin', 'rooms'],
    queryFn: () => apiFetch<{ rooms: RoomDoc[] }>('/api/manage/rooms'),
    enabled: signedIn,
  });

  const bindingsQ = useQuery({
    queryKey: ['admin', 'bindings'],
    queryFn: () => apiFetch<{ bindings: AdminBindingView[] }>('/api/manage/bindings'),
    enabled: signedIn,
    refetchInterval: 15_000,
  });

  const assignBindingMut = useMutation({
    mutationFn: (args: { id: string; roomId: string }) =>
      apiFetch<{ id: string; status: string; roomId: string; roomDisplayName: string }>(
        `/api/manage/bindings/${encodeURIComponent(args.id)}/assign-room`,
        { method: 'POST', body: JSON.stringify({ roomId: args.roomId }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bindings'] });
      setAssignBindingFor(null);
      setAssignRoomId('');
    },
  });

  const revokeBindingMut = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; status: 'revoked' }>(
        `/api/manage/bindings/${encodeURIComponent(id)}/revoke`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bindings'] });
      setRevokeBindingFor(null);
    },
  });

  const deleteBindingMut = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; deleted: true }>(
        `/api/manage/bindings/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bindings'] });
      setDeleteBindingFor(null);
    },
  });

  const commandBindingMut = useMutation({
    mutationFn: (args: { id: string; command: 'reload' | 'blank' | 'clear' }) =>
      apiFetch<{ id: string; command: string; queuedAt: string }>(
        `/api/manage/bindings/${encodeURIComponent(args.id)}/command`,
        { method: 'POST', body: JSON.stringify({ command: args.command }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bindings'] });
      setCommandBindingFor(null);
    },
  });

  const splitBindings = useMemo(() => {
    const list = bindingsQ.data?.bindings ?? [];
    return {
      unbound: list.filter((b) => b.status === 'unbound'),
      bound:   list.filter((b) => b.status === 'bound'),
      revoked: list.filter((b) => b.status === 'revoked'),
    };
  }, [bindingsQ.data]);

  const roomsByMap = useMemo(() => {
    const m = new Map<string, RoomDoc>();
    (roomsQ.data?.rooms ?? []).forEach((r) => m.set(r.roomId, r));
    return m;
  }, [roomsQ.data]);

  if (!signedIn) {
    return (
      <>
        <AdminNav />
        <section className="card"><p className="muted">Sign in on the Overview page first.</p></section>
      </>
    );
  }

  return (
    <>
      <AdminNav />

      <header className="admin-page-header">
        <h1>Devices</h1>
        <span className="muted" style={{ fontSize: '0.9rem' }}>
          Shared Web Clip <code>/kiosk</code> · claim-on-first-touch
        </span>
      </header>

      <section className="card">
        {bindingsQ.isLoading ? <p className="muted">Loading paired kiosks…</p> : null}
        {bindingsQ.error ? <p className="error">{(bindingsQ.error as AdminApiError).message}</p> : null}
        {bindingsQ.data ? (
          <>
            {splitBindings.unbound.length > 0 ? (
              <>
                <h3 style={{ marginBottom: '0.5rem' }}>
                  Unclaimed <span className="muted" style={{ fontWeight: 400 }}>({splitBindings.unbound.length})</span>
                </h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Claim code</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th>User agent</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {splitBindings.unbound.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: '1.4rem', letterSpacing: '0.1em' }}>
                            {b.claimCode}
                          </strong>
                        </td>
                        <td>{relativeTime(b.createdAt)}</td>
                        <td>{b.lastSeenAt ? relativeTime(b.lastSeenAt) : <span className="muted">—</span>}</td>
                        <td>
                          <span className="muted" style={{ fontSize: '0.85rem' }} title={b.userAgent ?? ''}>
                            {(b.userAgent ?? '').slice(0, 40)}
                            {(b.userAgent?.length ?? 0) > 40 ? '…' : ''}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => { setAssignBindingFor(b); setAssignRoomId(''); }}>Assign room</button>
                          {' · '}
                          <button className="link-button" onClick={() => setDeleteBindingFor(b)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}

            {splitBindings.bound.length > 0 ? (
              <>
                <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                  Bound <span className="muted" style={{ fontWeight: 400 }}>({splitBindings.bound.length})</span>
                </h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Claim code</th>
                      <th>Room</th>
                      <th>Last seen</th>
                      <th>Bound by</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {splitBindings.bound.map((b) => {
                      const room = b.roomId ? roomsByMap.get(b.roomId) : undefined;
                      return (
                        <tr key={b.id}>
                          <td><span className={`status-dot ${b.online ? 'is-online' : 'is-offline'}`} aria-label={b.online ? 'online' : 'offline'} /></td>
                          <td><code className="code-inline">{b.claimCode}</code></td>
                          <td>{room?.displayName ?? b.roomId}</td>
                          <td>{b.lastSeenAt ? <span title={b.lastSeenAt}>{relativeTime(b.lastSeenAt)}</span> : <span className="muted">never</span>}</td>
                          <td><span className="muted" style={{ fontSize: '0.85rem' }}>{b.boundBy ?? '—'}</span></td>
                          <td>
                            <button className="link-button" onClick={() => { setAssignBindingFor(b); setAssignRoomId(b.roomId ?? ''); }}>Re-assign</button>
                            {' · '}
                            <button className="link-button" onClick={() => { setCommandBindingFor(b); setChosenBindingCommand('reload'); }}>Send command</button>
                            {' · '}
                            <button className="link-button" onClick={() => setRevokeBindingFor(b)}>Revoke</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : null}

            {splitBindings.revoked.length > 0 ? (
              <>
                <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                  Revoked <span className="muted" style={{ fontWeight: 400 }}>({splitBindings.revoked.length})</span>
                </h3>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Claim code</th>
                      <th>Revoked</th>
                      <th>By</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {splitBindings.revoked.map((b) => (
                      <tr key={b.id} className="is-inactive">
                        <td><code className="code-inline">{b.claimCode}</code></td>
                        <td>{b.revokedAt ? <span title={b.revokedAt}>{relativeTime(b.revokedAt)}</span> : <span className="muted">—</span>}</td>
                        <td><span className="muted" style={{ fontSize: '0.85rem' }}>{b.revokedBy ?? '—'}</span></td>
                        <td>
                          <button className="link-button" onClick={() => { setAssignBindingFor(b); setAssignRoomId(''); }}>Re-bind</button>
                          {' · '}
                          <button className="link-button" onClick={() => setDeleteBindingFor(b)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}

            {splitBindings.unbound.length === 0 && splitBindings.bound.length === 0 && splitBindings.revoked.length === 0 ? (
              <p className="muted">
                No iPads have hit <code>/kiosk</code> yet. When one does, it'll appear here with a claim code.
              </p>
            ) : null}
          </>
        ) : null}
      </section>

      {/* Assign / re-assign room for a paired kiosk */}
      {assignBindingFor ? (
        <Modal
          title={assignBindingFor.status === 'bound' ? `Re-assign kiosk ${assignBindingFor.claimCode}` : `Assign room to kiosk ${assignBindingFor.claimCode}`}
          onClose={() => { setAssignBindingFor(null); setAssignRoomId(''); }}
        >
          {assignBindingFor.status === 'bound' ? (
            <p>
              This kiosk is currently bound to{' '}
              <strong>{roomsByMap.get(assignBindingFor.roomId ?? '')?.displayName ?? assignBindingFor.roomId}</strong>.
              Choose a new room; the iPad will pick up the change on its next poll (within ~2 min).
            </p>
          ) : (
            <p>
              The iPad showing claim code <code className="code-inline">{assignBindingFor.claimCode}</code> will
              flip to the chosen room's kiosk view within a few seconds.
            </p>
          )}
          <form
            className="admin-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!assignRoomId) return;
              assignBindingMut.mutate({ id: assignBindingFor.id, roomId: assignRoomId });
            }}
          >
            <label>
              <span>Room</span>
              <select value={assignRoomId} onChange={(e) => setAssignRoomId(e.target.value)} required>
                <option value="">— pick a room —</option>
                {(roomsQ.data?.rooms ?? []).filter((r) => r.active).map((r) => (
                  <option key={r.roomId} value={r.roomId}>{r.displayName}</option>
                ))}
              </select>
            </label>
            {assignBindingMut.error ? <p className="error">{(assignBindingMut.error as AdminApiError).message}</p> : null}
            <div className="admin-form-actions">
              <button type="button" className="button-ghost" onClick={() => { setAssignBindingFor(null); setAssignRoomId(''); }}>Cancel</button>
              <button type="submit" disabled={assignBindingMut.isPending || !assignRoomId}>
                {assignBindingMut.isPending ? 'Saving…' : (assignBindingFor.status === 'bound' ? 'Re-assign' : 'Assign')}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {/* Queue a command on a paired kiosk */}
      {commandBindingFor ? (
        <Modal title={`Queue a command on ${commandBindingFor.claimCode}`} onClose={() => setCommandBindingFor(null)}>
          <p className="muted">
            The kiosk picks the command up on its next status poll (≤ 30s).
          </p>
          <label>
            <span>Command</span>
            <select value={chosenBindingCommand} onChange={(e) => setChosenBindingCommand(e.target.value as typeof chosenBindingCommand)}>
              {COMMANDS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <small className="muted">
              <strong>reload</strong> — force the kiosk to reload the page.{' '}
              <strong>blank</strong> — black out the screen.{' '}
              <strong>clear</strong> — un-blank.
            </small>
          </label>
          {commandBindingMut.error ? <p className="error">{(commandBindingMut.error as AdminApiError).message}</p> : null}
          <div className="admin-form-actions">
            <button type="button" className="button-ghost" onClick={() => setCommandBindingFor(null)}>Cancel</button>
            <button type="button" disabled={commandBindingMut.isPending}
              onClick={() => commandBindingMut.mutate({ id: commandBindingFor.id, command: chosenBindingCommand })}>
              {commandBindingMut.isPending ? 'Queuing…' : 'Queue command'}
            </button>
          </div>
        </Modal>
      ) : null}

      {/* Revoke a paired kiosk */}
      {revokeBindingFor ? (
        <Modal title={`Revoke kiosk ${revokeBindingFor.claimCode}?`} onClose={() => setRevokeBindingFor(null)}>
          <p>
            This kiosk will stop showing its room and will show a{' '}
            <strong>"Deauthorized — Contact IT"</strong> screen within ~2 minutes.
          </p>
          <p className="muted">
            Use this for a lost or stolen iPad, or to take a kiosk out of service. You can re-bind a revoked
            kiosk later if it comes back.
          </p>
          {revokeBindingMut.error ? <p className="error">{(revokeBindingMut.error as AdminApiError).message}</p> : null}
          <div className="admin-form-actions">
            <button type="button" className="button-ghost" onClick={() => setRevokeBindingFor(null)}>Cancel</button>
            <button type="button" disabled={revokeBindingMut.isPending} onClick={() => revokeBindingMut.mutate(revokeBindingFor.id)}>
              {revokeBindingMut.isPending ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </Modal>
      ) : null}

      {/* Delete a paired kiosk record (unbound orphan or revoked audit cleanup).
          Bound bindings can't be deleted directly — they must be revoked first. */}
      {deleteBindingFor ? (
        <Modal
          title={
            deleteBindingFor.status === 'unbound'
              ? `Delete unclaimed kiosk ${deleteBindingFor.claimCode}?`
              : `Delete revoked kiosk ${deleteBindingFor.claimCode}?`
          }
          onClose={() => setDeleteBindingFor(null)}
        >
          {deleteBindingFor.status === 'unbound' ? (
            <p>
              No iPad is bound to this code. If the iPad that originally claimed it is still showing this
              code, it'll re-claim a new code on its next poll and reappear in Unclaimed. Use this to clear
              orphan rows from dev / smoke tests / cleared-Safari iPads.
            </p>
          ) : (
            <p>
              Permanently remove this revoked kiosk record. If the iPad ever comes back online with its old
              token, it'll claim a fresh code and appear in the Unclaimed list.
            </p>
          )}
          {deleteBindingMut.error ? <p className="error">{(deleteBindingMut.error as AdminApiError).message}</p> : null}
          <div className="admin-form-actions">
            <button type="button" className="button-ghost" onClick={() => setDeleteBindingFor(null)}>Cancel</button>
            <button type="button" disabled={deleteBindingMut.isPending} onClick={() => deleteBindingMut.mutate(deleteBindingFor.id)}>
              {deleteBindingMut.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  const diff = Date.now() - then;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
